<template>
  <div
    v-click-outside="closeSizeDropdown"
    class="product-variant-selector"
    :class="[
      { 'is-loading' : states.component !== 'ready' },
      { 'is-sold-out' : selectedVariantSoldOut },
      { 'is-product-card' : context === 'product-card' }
    ]"
  >
    <span
      v-if="showLengthOptionSelector && lengthHeading"
      class="
        product-variant-selector__title
        product-variant-selector__title-length
      "
      v-text="lengthHeading"
    />

    <div
      v-if="showLengthOptionSelector"
      class="
        dropdown-select
        product-variant-selector__button-length
        is-inline
      "
      :data-label="lengthLabel"
    >
      <ul class="dropdown-select__list product-variant-selector__length">
        <li
          v-for="(value) in lengthOptions.values"
          :key="getOptionKey(value)"
          class="
            dropdown-select__list-item
            product-variant-selector__item-length
          "
          :class="{
            'is-selected': getOptionSelectedState(lengthOptions.name, value),
            'is-sold-out': !getOptionAvailability(lengthOptions.name, value),
          }"
        >
          <input
            :id="getInputId(value)"
            type="radio"
            class="
              product-variant-selector__input
              product-variant-selector__input-length
            "
            :checked="getOptionSelectedState(lengthOptions.name, value)"
            :disabled="states.component !== 'ready'"
            :name="getOptionValueName(lengthOptions)"
            :value="value"
            @change="handleOptionChange(lengthOptions.name, value, true)"
          >

          <label
            :for="getInputId(value)"
            class="
              product-variant-selector__label
              product-variant-selector__label-length
            "
          >
            <span
              class="product-variant-selector__label-text"
              v-html="value"
            />
          </label>
        </li>
      </ul>
    </div>

    <div
      v-if="filteredBadges?.length"
      class="product-variant-selector__badges product-badges"
    >
      <span
        v-for="(badge, index) in filteredBadges"
        :key="`${badge.name}-${index}`"
        class="product-variant-selector__badge product-badge"
        v-text="badge.name"
      />
    </div>

    <span
      v-if="sizeOptions"
      class="
        product-variant-selector__title
        product-variant-selector__title-size
      "
      v-text="sizeHeading"
    />

    <span
      v-if="showErrorMessage"
      class="product-variant-selector__error-message"
      v-text="errorMessageText"
    />

    <div class="product-variant-selector__size-container">
      <div
        v-if="sizeOptions"
        ref="sizeDropdown"
        class="dropdown-select"
        :class="{
          'is-active': states.sizeDropdown === 'open'
        }"
      >
        <btn
          v-if="context !== 'product-card'"
          class="
            dropdown-select__button
            button--primary button--block
            product-variant-selector__button-size
          "
          :label="sizeButtonText"
          icon="icon-down"
          :disable-button="disableSizeSelection"
          @click.prevent="toggleSizeDropdown"
        />

        <transition name="fade">
          <ul
            v-if="states.sizeDropdown === 'open'"
            class="dropdown-select__list product-variant-selector__size"
            :style="{ '--label-text-max-width': maxLabelWidth + 'px' }"
          >
            <li
              v-for="(value) in sizeOptions.values"
              :key="getOptionKey(value)"
              class="dropdown-select__list-item product-variant-selector__item-size"
              :class="{
                'is-disabled': getSizeOptionDisableState(
                  sizeOptions.name,
                  value
                ),
                'is-low-stock': getOptionLowStockState(sizeOptions.name, value),
                'is-selected': getOptionSelectedState(sizeOptions.name, value),
                'is-sold-out': !getOptionAvailability(sizeOptions.name, value),
                'is-out-of-stock': getOptionOutOfStockState(
                  sizeOptions.name,
                  value
                ),
              }"
            >
              <input
                :id="getInputId(value)"
                type="radio"
                class="
                  product-variant-selector__input
                  product-variant-selector__input-size
                "
                :checked="getOptionSelectedState(sizeOptions.name, value)"
                :disabled="
                  states.component !== 'ready' ||
                    getOptionOutOfStockState(sizeOptions.name, value)
                "
                :name="getOptionValueName(sizeOptions)"
                :value="value"
                @change="handleOptionChange(sizeOptions.name, value, true)"
              >

              <label
                :for="getInputId(value)"
                class="
                  product-variant-selector__label
                  product-variant-selector__label-size
                "
              >
                <span
                  ref="labelTextEls"
                  class="product-variant-selector__label-text"
                  v-html="getSizeLabel(sizeOptions.name, value)"
                />
              </label>
            </li>
          </ul>
        </transition>
      </div>

      <button
        v-if="displayAddToCart"
        ref="addToCart"
        class="
          product-variant-selector__add-to-cart
          button
          button--black
        "
        type="submit"
        @click.prevent="handleAddToCart"
        v-html="addToCartLabel"
      />

      <div class="product-variant-selector__wishlist">
        <wishlist-button
          v-if="displayWishlist"
          :current-variant="currentVariant"
          :product="product"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Component: Product Variant Selector
 * ------------------------------------------------------------------------------
 * - Core component for the variant selector.
 * @param {String} [context] - Location of the form, for style amends.
 * @param {Object} currentVariant - Current Variant, set from parent.
 * @param {Boolean} [displayAddToCart] - Display an inline 'Add to cart' button.
 * @param {Boolean} [displayWishlist] - Display an inline 'Wishlist' button.
 * @param {Boolean|String} [lengthHeadingText] - Optional override for the Length heading.
 * @param {String} parentState - Loading state of the parent component.
 * @param {Object} product - Formatted Shopify product.
 * @param {Boolean|Array} filteredBadges - Badges to display above the size selector.
 *
 */
import { mapState } from 'pinia'

import { useScreenStore } from '~stores/screen'

import { importComponent } from '~/core/mint/helpers'

import classes from '~/helpers/classes'
import { stringToHandle } from '~/helpers/strings'
import { arrayObjectsEqual } from '~/helpers/utils'

import Btn from '~components/btn/btn'

export default {
  name: 'ProductVariantSelector',

  components: {
    Btn,
    WishlistButton: importComponent('wishlist-button'),
  },

  props: {
    context: {
      type: String,
      default: 'product-form',
    },
    currentVariant: {
      type: [Boolean, Object],
      default: false,
    },
    displayAddToCart: {
      type: Boolean,
      default: false,
    },
    displayWishlist: {
      type: Boolean,
      default: false,
    },
    lengthHeadingText: {
      type: [Boolean, String],
      default: false,
    },
    parentState: {
      type: String,
      default: 'loading',
    },
    product: {
      type: Object,
      required: true,
    },
    filteredBadges: {
      type: [Boolean, Array],
      default: false,
    },
  },

  emits: [
    'add-product-to-cart',
    'variant-updated',
    'close-size-dropdown',
  ],

  data() {
    return {
      lastUpdatedOption: false,
      maxLabelWidth: 0,
      selectedOptions: [],
      states: {
        errorMessage: 'hidden',
        component: 'loading',

        /**
         * - Sizes are always visible within product cards by default, until the
         * product is added to cart / selector toggled
         */
        sizeDropdown: this.context === 'product-card' ? 'open' : 'closed',
        userHasManuallySelected: false,
      },
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
      viewportWidth: 'width',
    }),

    /**
     * Calculate if the current selection is sold out.
     * - Checks if the customer has previously made a selection to prevent a
     *   false positive showing when a 'Size' has been selected, but not 'Length'
     * @returns {Boolean}
     */
    selectedVariantSoldOut() {
      if (!this.lastUpdatedOption || !this.currentVariant?.id) {
        return false
      }

      return Boolean(this.selectedOptions.length && !this.currentVariant.available)
    },

    /**
     * Calculate if to display the 'Length' option selector.
     * @returns {Boolean}
     */
    showLengthOptionSelector() {
      return Boolean(this.lengthOptions?.values?.length)
    },

    /**
     * Calculate if to display the error message element.
     * @returns {Boolean}
     */
    showErrorMessage() {

      /**
       * Local error message state is true, likely invalid variant selection
       */
      if (this.states.errorMessage === 'visible') {
        return true
      }

      /**
       * Parent error, likely Shopify API error.
       */
      return Boolean(this.parentState === 'error')
    },

    /**
     * Calculate the message text, depending on type of error.
     * @returns {String}
     */
    errorMessageText() {
      const {
        cartAddError,
        outOfStockError,
        selectSizeError,
      } = mint.strings.productDetailPage.productForm

      /**
       * Customer has not selected a size.
       */
      if (!this.customerHasSelectedSize) {
        return selectSizeError
      }

      /**
       * Product is unavailable.
       */
      if (!this.product.available) {
        return outOfStockError
      }

      /**
       * Otherwise, display a generic cart error message.
       * - Typically when Shopify returns an error from the Cart API.
       * - This will be set in the parent component.
       */
      return cartAddError
    },

    /**
     * Calculate if the customer has selected a 'Size' option.
     * @returns {Boolean}
     */
    customerHasSelectedSize() {
      return this.selectedOptions.some((option) => {
        return this.isSizeOption(option.name)
      })
    },

    /**
     * Calculate if to disable 'Size Selection' dropdown from opening.
     * - on Product Cards, if both Length and Size options are present,
     * the user *must* select 'Length' first, as current UX means that
     * the product is added to the cart as soon as 'Size' is selected.
     * @returns {Boolean}
     */
    disableSizeSelection() {
      if (
        !this.showLengthOptionSelector ||
        this.context !== 'product-card' ||
        this.context !== 'product-form'
      ) {
        return false
      }

      const userSelectedLength = this.selectedOptions.find((option) => {
        return this.isLengthOption(option.name)
      })

      return Boolean(!userSelectedLength)
    },

    /**
     * Calculate the 'Length' options for a given product.
     * @returns {Object}
     */
    lengthOptions() {
      if (!this.product.options) {
        return false
      }

      const options = this.product.options?.find((option) => {
        return this.isLengthOption(option.name)
      })

      return options || []
    },

    /**
     * Calculate the 'Size' options for a given product.
     * @returns {Object}
     */
    sizeOptions() {
      if (!this.product.options) {
        return false
      }

      const options = this.product.options?.find((option) => {
        return this.isSizeOption(option.name)
      })

      return options || []
    },

    /**
     * Calculate the text to display on form error.
     * @returns {String}
     */
    sizeErrorText() {
      return 'Hello Cart Error'
    },

    /**
     * Calculate the text for the 'Length' dropdown button.
     * @returns {String}
     */
    lengthButtonText() {
      const selectedLength = this.selectedOptions.find((selected) => {
        return this.isLengthOption(selected.name)
      })

      if (selectedLength) {
        return selectedLength.value
      }

      return mint.strings.products.productForm.selectLengthButton
    },

    /**
     * Calculate text heading for the title.
     * @returns {String}
     */
    sizeHeading() {
      if (
        !mint.flags.international.showLocalisedSizes &&
        mint.shop.country !== 'GB'
      ) {
        return mint.strings.products.productForm.selectSizeHeadingInternational
      }

      return mint.strings.products.productForm.selectSizeHeading
    },

    /**
     * Calculate button text for 'Add To Cart' button.
     * @returns {String}
     */
    addToCartLabel() {
      if (this.parentState === 'addingToBag') {
        return `
          <span class="ico ico-spinner">
            <span></span>
          </span>
        `
      }

      return mint.strings.productDetailPage.productForm.addToCart
    },

    /**
     * Calculate text heading for the title.
     * @returns {String}
     */
    lengthHeading() {

      /**
       * Don't show 'Length' label within product cards.
       */
      if (this.context === 'product-card') {
        return ''
      }

      if (this.lengthHeadingText) {
        return this.lengthHeadingText
      }

      const {
        selectLengthHeading,
        selectedLengthHeading,
      } = mint.strings.products.productForm

      let label = selectLengthHeading

      const desktopProductCard = Boolean(
        this.context === 'product-card' && !this.isMobile,
      )

      if (!desktopProductCard) {
        return label
      }

      const selectedLength = this.selectedOptions.find((selected) => {
        return this.isLengthOption(selected.name)
      })

      if (selectedLength) {
        label = selectedLengthHeading.replace(/{{ length }}/g, selectedLength.value)
      }

      return label
    },

    /**
     * Calculate the text for the 'Size' dropdown button.
     * @returns {String}
     */
    sizeButtonText() {
      const selectedSize = this.selectedOptions.find((selected) => {
        return this.isSizeOption(selected.name)
      })

      if (selectedSize) {
        return selectedSize.value
      }

      if (
        !mint.flags.international.showLocalisedSizes &&
        mint.shop.country !== 'GB'
      ) {
        return mint.strings.products.productForm.selectSizeButtonInternational
      }

      return mint.strings.products.productForm.selectSizeButton
    },

    /**
     * Calculate `Size` label for the size selector.
     * @returns {String}
     */
    sizeLabel() {
      return mint.strings.products.productForm.sizeLabel
    },

    /**
     * Calculate `Length` label for the length selector.
     * @returns {String}
     */
    lengthLabel() {
      return mint.strings.products.productForm.lengthLabel
    },

    /**
     * Calculate `Sold out` text.
     * @returns {String}
     */
    soldOutText() {
      return mint.strings.products.outOfStock
    },
  },

  watch: {

    /**
     * Watch the current variant ID.
     * - Used only on mount, oldValue will be empty.
     * - This is used to pre-select variants in the form.
     */
    'currentVariant.id': {
      handler(newValue, oldValue) {

        /**
         * If there was a previously set currentVariant and it is removed,
         * set the product form to its default state.
         * - Product Form should reset to defaults after successful Add to Bag.
         */
        if (oldValue && typeof !newValue) {
          this.resetCurrentVariant()
          this.setDefaultOptionSelection()
          return
        }

        /**
         * - If no 'oldValue', then assume this is executing on page-load.
         */
        if (oldValue) {
          return
        }

        this.setDefaultOptionSelection()
      },
    },

    /**
     * The product has been updated.
     * - Will execute on initial page load
     * - Will execute on swatch update.
     */
    'product.key': {
      handler() {
        this.resetCurrentVariant()
        this.setDefaultOptionSelection()

        this.states.sizeDropdown = this.context === 'product-card' ? 'open' : 'closed'
      },
    },

    /**
     * The Selected Options have changed.
     * - Set an active variant based on matching Option Values.
     * - Emit the variant to the parent component.
     */
    selectedOptions: {
      handler() {

        /**
         * No early return if no `matchingVariant`
         * - Sending a false to pinia will reset the variant and ?variant= URL.
         * - Use case: Product Form resets after successful Add To Bag.
         */
        const matchingVariant = this.getVariantFromOptions(this.selectedOptions)

        this.states.errorMessage = 'hidden'
        this.states.sizeDropdown = this.context === 'product-card' ? 'open' : 'closed'

        /**
         * Reset the form when variant is OOS.
         * - Back in Stock will be triggered on variant selection
         * - Prevent customer from adding item to the basket.
         * !IMPORTANT:
         * - Variant should be reset upon product form only, as it causes
         * unnecessary variables mutations (including the one we're watching),
         * leads to double emitting event below and accordingly triggers swym
         * popup twice (when BIS is enabled).
         * - nextTick() used to ensure selectedVariantSoldOut is evaluated after the DOM updates.
         */

        this.$nextTick(() => {
          if (this.selectedVariantSoldOut && this.context === 'product-form') {
            this.resetCurrentVariant()
          }
        })

        this.$emit('variant-updated', {
          currentVariant: matchingVariant,
          isManualSelection: this.userHasManuallySelected,
          lastUpdatedOption: this.lastUpdatedOption,
        })
      },
      deep: true,
    },

    /**
     * The user has resized their screen.
     */
    viewportWidth() {
      this.setMaxLabelTextWidth()
    },

    'states.sizeDropdown': {
      handler(newValue) {
        if (newValue !== 'open') {
          return
        }

        this.setMaxLabelTextWidth()
      },
    },
  },

  mounted() {

    /**
     * Component and state/product initialisation can happen in mount (if the product
     * object is ready and being passed in as a prop) and the `product.key` watcher,
     * as this key is set once the product object changes
     */

    if (!this.product?.handle) {
      return
    }

    this.setDefaultOptionSelection()
    this.setMaxLabelTextWidth()

    this.states.component = 'ready'
  },

  methods: {

    /**
     * Handle events for when the customer sets
     * @param {String} name - Name of a given variant option (e.g. 'Size')
     * @param {String} value - Value of a given option selection (e.g. 'Small')
     * @param {Boolean} [isManualSelection] - If the user has manually interacted with the selection.
     */
    handleOptionChange(name, value, isManualSelection = false) {
      const newSelected = {
        name,
        value,
      }

      const optionExists = this.selectedOptions.find((selected) => {
        return selected.name === name
      })

      if (optionExists) {
        this.selectedOptions = this.selectedOptions.filter((selected) => {
          return selected.name !== name
        })
      }

      if (
        this.isSizeOption(name) &&
        this.currentVariant.available
      ) {
        this.states.sizeDropdown = 'closed'
      }

      /**
       * Define if the user has manually interacted with the variant selector.
       * - Prevents 'Back in Stock' modal from auto-triggering on
       * page load when ?variant= param present
       * - Prevents bug where BIS modal is triggered twice
       */
      this.userHasManuallySelected = isManualSelection

      /**
       * Update options.
       */
      this.lastUpdatedOption = name
      this.selectedOptions.push(newSelected)
    },

    /**
     * Handle adding a product to the cart.
     * - Emits event to a parent, where logic can take place there
     * - In this case, ProductCard and ProductForm will handle this
     * - Event is emitted to reduce logic in ProductVariantSelector
     * @fires ProductVariantSelector#add-product-to-cart
     */
    handleAddToCart() {
      let errorState = 'hidden'
      let allowAddToCart = true

      if (
        !this.customerHasSelectedSize ||
        this.product.restricted
      ) {
        errorState = 'visible'
        allowAddToCart = false
      }

      /**
       * Hide any previous errors.
       */
      this.states.errorMessage = errorState

      if (!allowAddToCart) {
        return
      }

      /**
       * Proceed with adding to basket.
       */
      this.$emit('add-product-to-cart', {
        currentVariant: this.currentVariant,
      })
    },

    /**
     * Handle default variant and option selections.
     * - For 'ONE SIZE' - select the first variant
     *
     * - If currentVariant exists, set both selectors
     * (Useful for PDP pages loaded with `?variant=XXX` in the URL)
     *
     * - If a product has 'Length' options as well as 'Size', a default
     *   option should be set. Attempt 'Regular' but fallback to first index.
     *
     */
    setDefaultOptionSelection() {
      if (!this.product?.variants?.length) {
        return
      }

      /**
       * If the product is only 'ONE SIZE' or 'Default Size',
       * do an early return to ensure the default option isn't selected
       * - This is so the UX allows for 'Notify me' to work when selecting a size
       * - Creative have said they'll revisit this.
       */
      if (this.product.onlyHasDefaultVariant) {
        return
      }

      /**
       * If a current variant is active, (e.g. navigating straight to a URL
       * with `?variant=XXX` parameter), then preselect each relevant option.
       */
      if (this.currentVariant && this.context === 'product-form') {
        for (const selectedOption of this.currentVariant.selectedOptions) {
          this.handleOptionChange(selectedOption.name, selectedOption.value)
        }

        return
      }

      /**
       * If leg lengths are available, ensure at least one option is pre-selected.
       * - Try to default to 'Regular', but if not, fallback to first index.
       */
      if (this.showLengthOptionSelector) {
        let targetIndex = 0

        const regularSizeIndex = this.lengthOptions.values.findIndex((value) => {
          return value === 'Regular'
        })

        if (regularSizeIndex > -1) {
          targetIndex = regularSizeIndex
        }

        this.lengthOptions.values[targetIndex]

        this.handleOptionChange(
          this.lengthOptions.name,
          this.lengthOptions.values[targetIndex],
        )
      }
    },

    /**
     * Remove the selected variant entirely.
     * - This will trigger the parent components to remove currentVariant
     *   and reset the form to its default state.
     */
    resetCurrentVariant() {
      this.lastUpdatedOption = false
      this.selectedOptions = []
    },

    /**
     * Toggle the visibility of the size selector.
     */
    toggleSizeDropdown() {
      this.states.sizeDropdown = this.states.sizeDropdown === 'open'
        ? 'closed'
        : 'open'
    },

    /**
     * Explicitly close the Size Dropdown.
     * - Used for v-click-outside as it breaks if you try and run
     *   this.toggleSizeDropdown() with a parameter, e.g.
     *   toggleSizeDropdown('closed')
     */
    closeSizeDropdown() {
      this.states.sizeDropdown = 'closed'
      this.$emit('close-size-dropdown')
    },

    /**
     * Return a unique ID that can be used to identify inputs and labels
     * - Used to ensure tapping a label toggles the input value.
     * @param {String} value - Variant Option value
     * @returns {String}
     */
    getInputId(value) {
      return `${this.product.key}-${stringToHandle(value)}`
    },

    /**
     * Return a formatted string for the 'Length' input text.
     * - For Product Cards, design indicates only the first letter is displayed
     * e.g. 'Regular' will only display 'R'
     * @param {String} value - Variant Option value
     * @returns {String}
     */
    getLengthButtonText(value = '') {
      if (this.context === 'product-card') {
        return value.charAt(0)
      }

      return value
    },

    /**
     * Return a boolean for if a given option is available.
     * - Compares against this.selectedOptions to deduct the active variant.
     * - UX dictates that the 'Sold out' state is only added to 'Size' lists.
     * - If a product has both Length and Size (e.g. trousers), the 'Size'
     *   dropdown is typically disabled until the customer selects a 'Length'
     * @param {String} name - Name of a given variant option (e.g. 'Size')
     * @param {String} value - Value of a given option selection (e.g. 'Small')
     * @return {Boolean}
     */
    getOptionAvailability(name, value) {
      const formattedOptions = []

      formattedOptions.push({
        name, value,
      })

      if (this.showLengthOptionSelector) {
        const selectedLength = this.selectedOptions.find((selected) => {
          return this.isLengthOption(selected.name)
        })

        if (selectedLength) {
          formattedOptions.push(selectedLength)
        }
      }

      const matchingVariant = this.getVariantFromOptions(formattedOptions)

      if (!matchingVariant) {
        return false
      }

      return Boolean(matchingVariant.available)
    },

    /**
     * Return a boolean for if the 'Size' options should be disabled.
     * @returns {Boolean}
     */
    getSizeOptionDisableState() {
      const selectedLength = this.selectedOptions.find((selected) => {
        return this.isLengthOption(selected.name)
      })

      return Boolean(this.showLengthOptionSelector && !selectedLength)
    },

    /**
     * Return a boolean for if a given option should be marked as 'Low stock'
     * @param {String} name - Name of a given variant option (e.g. 'Size')
     * @param {String} value - Value of a given option selection (e.g. 'Small')
     * @return {Boolean}
     */
    getOptionLowStockState(name, value) {
      if (!mint.products.lowStock) {
        return false
      }

      const formattedOptions = []

      formattedOptions.push({
        name, value,
      })

      if (this.showLengthOptionSelector) {
        const selectedLength = this.selectedOptions.find((selected) => {
          return this.isLengthOption(selected.name)
        })

        if (selectedLength) {
          formattedOptions.push(selectedLength)
        }
      }

      const matchingVariant = this.getVariantFromOptions(formattedOptions)

      if (!matchingVariant) {
        return false
      }

      return Boolean(
        matchingVariant.inventory >= 1 &&
          mint.products.lowStockThreshold >= matchingVariant.inventory,
      )
    },

    /**
     * Return a boolean for if a given option is out of stock.
     * - Checks whether the option is unavailable.
     * - Checks whether the Back in Stock is disabled for given product.
     * @param {String} name - Name of a given variant option (e.g. 'Size')
     * @param {String} value - Value of a given option selection (e.g. 'Small')
     * @return {Boolean}
     */
    getOptionOutOfStockState(name, value) {
      return (
        !this.getOptionAvailability(name, value) &&
        !this.getBackInStockState(name, value)
      )
    },

    /**
     * Return a variant object from a given set of options.
     * @param {Array} options - Options, must match this.selectedOptions format.
     * @return {Boolean|Object}
     */
    getVariantFromOptions(options = []) {
      if (!this.product || !this.product?.variants) {
        return false
      }

      const matchingVariant = this.product.variants.find((variant) => {
        return arrayObjectsEqual(options, variant.selectedOptions)
      })

      return matchingVariant ? matchingVariant : false
    },

    /**
     * Return a unique key identifier for each v-for loop.
     * @param {Object} option - Formatted option object.
     * @returns {String}
     */
    getOptionKey(option) {
      return `${this.product.key}-${stringToHandle(option.name)}`
    },

    /**
     * Return a boolean for a name/value combination if the user has selected it.
     * @param {String} name - Name of a given variant option (e.g. 'Size')
     * @param {String} value - Value of a given option selection (e.g. 'Small')
     * @return {Boolean}
     */
    getOptionSelectedState(name, value) {
      const isSelected = this.selectedOptions.find((selected) => {
        return Boolean(
          selected.name === name &&
          selected.value === value,
        )
      })

      return Boolean(isSelected)
    },

    /**
     * Return a unique name attribute identifier for an input.
     * @param {Object} option - Formatted option object.
     */
    getOptionValueName(option) {
      return `${this.product.key}-${stringToHandle(option.name)}`
    },

    /**
     * Return a boolean if the product should display 'Back in Stock' config.
     * @param {String} name - Name of a given variant option (e.g. 'Size')
     * @param {String} value - Value of a given option selection (e.g. 'Small')
     * @returns {Boolean}
     */
    getBackInStockState(name, value) {
      const isAvailable = this.getOptionAvailability(name, value)
      const enableBackInStock = this.product?.wishlist?.bisEnabled

      return Boolean(!isAvailable && enableBackInStock)
    },

    /**
     * Return a HTML label for the 'Size' option dropdown.
     * @param {String} name - Name of a given variant option (e.g. 'Size')
     * @param {String} value - Value of a given option selection (e.g. 'Small')
     */
    getSizeLabel(name, value) {
      const isAvailable = this.getOptionAvailability(name, value)
      const isBisEnabled = this.getBackInStockState(name, value)
      const isLowStock = this.getOptionLowStockState(name, value)

      const selectedLength = this.selectedOptions.find((selected) => {
        return this.isLengthOption(selected.name)
      })

      /**
       * If Length options exist and none have been selected, show default.
       */
      if (this.showLengthOptionSelector && !selectedLength) {
        return value
      }

      /**
       * If the product is available but is low stock, display message.
       */
      if (isAvailable && isLowStock) {
        return `
          <span>${value}</span>
          <span class="product-variant-selector__low-stock">
            ${mint.strings.products.productForm.lowStockText}
          </span>
        `
      }

      /**
       * If the product has marked to have a 'Notify me' trigger, show that.
       * Do not allow 'Back in Stock' if the product has the 'swym-disabled' tag.
       */
      if (isBisEnabled) {
        const notifyMeText = this.product.comingSoon
          ? mint.Strings('products.product_form.notify_me_for_coming_soon')
          : mint.Strings('products.product_form.notify_me_button')

        return `
          <s>${value}</s>
          <span class="product-variant-selector__notify-me">
            ${notifyMeText}
          </span>
        `
      }

      /**
       * If sold out, display strike.
       */
      if (!isAvailable) {
        const outOfStockText = mint.Strings('products.product_form.out_of_stock_text')

        return `
          <s>${value}</s>
          <span class="product-variant-selector__out-of-stock">
            ${outOfStockText}
          </span>
        `
      }

      /**
       * Else, display default.
       */
      return value
    },

    /**
     * Identify length options case-insensitively.
     * @param {String} optionName
     * @returns Boolean
     */
    isLengthOption(optionName = '') {
      return stringToHandle(optionName) === 'length'
    },

    /**
     * Identify size options case-insensitively.
     * @param {String} optionName
     * @returns Boolean
     */
    isSizeOption(optionName = '') {
      return stringToHandle(optionName) === 'size'
    },

    /**
     * Set a max width of the label text across size options.
     * - This ensures stock messaging is aligned vertically regardless of option
     * length.
     */
    setMaxLabelTextWidth() {
      this.maxLabelWidth = 0

      this.$nextTick(() => {
        // If v-for is used, Vue makes $refs.labelTextEls an array
        const labelEls = this.$refs.labelTextEls

        if (!labelEls) {
          return
        }

        const labelsArray = Array.isArray(labelEls) ? labelEls : [labelEls]
        const widths = labelsArray.map((el) => el?.offsetWidth || 0)
        this.maxLabelWidth = Math.max(...widths)
      })
    },
  },
}
</script>
